import React from 'react';
import { motion } from 'framer-motion';
import { FaCar, FaRoad, FaInfoCircle } from 'react-icons/fa';
import { FiSettings, FiMapPin } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const steps = [
  {
    id: 1,
    title: 'Choose Your Service',
    desc: 'Select the lesson or test-preparation service that best matches your current driving goals.',
    icon: FiSettings
  },
  {
    id: 2,
    title: 'Meet Your Instructor',
    desc: 'Meet your instructor at the confirmed location and discuss your experience, goals and areas of focus.',
    icon: FaCar
  },
  {
    id: 3,
    title: 'Build Your Confidence',
    desc: 'Receive calm, structured guidance designed around your current skill level and next driving milestone.',
    icon: FaRoad
  }
];

const MAP_LINK = 'https://maps.app.goo.gl/jFsf5zKT3sU3UdDJA';

export default function HowItWorks() {
  return (
    <section
      id="how-it-works"
      className="relative overflow-hidden bg-brand-darkbg py-24"
    >
      <div className="container mx-auto px-4 md:px-6">
        <div className="mx-auto mb-16 max-w-3xl text-center">
          <h2 className="mb-4 font-display text-4xl text-white md:text-5xl">
            Getting Started is <span className="text-brand-gold">Easy.</span>
          </h2>
          <div className="mx-auto h-1.5 w-24 rounded-full bg-brand-gold" />
        </div>

        <div className="relative mx-auto max-w-5xl">
          <div className="absolute left-0 top-1/2 z-0 hidden h-1 w-full -translate-y-1/2 bg-brand-gold/20 md:block" />

          <div className="relative z-10 grid grid-cols-1 gap-12 md:grid-cols-3">
            {steps.map((step, index) => (
              <motion.div
                key={step.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="flex flex-col items-center text-center"
              >
                <div className="relative mb-6">
                  <div className="absolute -right-2 -top-2 z-20 flex h-8 w-8 items-center justify-center rounded-full border-2 border-brand-gold bg-brand-surface font-display text-brand-gold">
                    {step.id}
                  </div>
                  <div className="flex h-24 w-24 items-center justify-center rounded-full border-4 border-brand-gold bg-brand-surface text-4xl text-brand-gold shadow-xl transition-transform hover:rotate-12">
                    <SafeIcon icon={step.icon} />
                  </div>
                </div>

                <h3 className="mb-3 font-display text-2xl text-white">
                  {step.title}
                </h3>
                <p className="px-4 leading-relaxed text-white/70">
                  {step.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="mx-auto mt-20 flex max-w-4xl flex-col items-center gap-4 rounded-2xl border-2 border-white/20 bg-brand-green p-6 text-white shadow-lg md:flex-row"
        >
          <div className="shrink-0 rounded-full bg-white/20 p-3">
            <SafeIcon icon={FaInfoCircle} className="text-2xl" />
          </div>
          <p className="flex-1 text-center text-lg font-medium md:text-left">
            Your meeting point and vehicle requirements depend on the service
            and instructor selected. Pre-test sessions generally begin near the{' '}
            <a
              href={MAP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 font-bold underline decoration-brand-gold underline-offset-4 transition-colors hover:text-brand-gold"
            >
              Service NSW Goulburn test centre
              <SafeIcon icon={FiMapPin} className="text-base text-brand-gold" />
            </a>
            .
          </p>
        </motion.div>
      </div>
    </section>
  );
}